import { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";

export const Timeline = ({ duration, captions, setCaptions, time, seek, selectedId, setSelectedId }) => {
  const [zoom, setZoom] = useState(50);
  const scrollRef = useRef(null);
  const drag = useRef(null);
  const width = Math.max(1, duration) * zoom;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const x = time * zoom;
    if (x < el.scrollLeft || x > el.scrollLeft + el.clientWidth - 40) el.scrollLeft = Math.max(0, x - 60);
  }, [time, zoom]);

  useEffect(() => {
    const move = (e) => {
      const d = drag.current;
      if (!d) return;
      const dt = (e.clientX - d.x0) / zoom;
      if (Math.abs(e.clientX - d.x0) > 3) d.moved = true;
      setCaptions((caps) => {
        const i = caps.findIndex((c) => c.id === d.id);
        if (i < 0) return caps;
        const lo = i > 0 ? caps[i - 1].end : 0;
        const hi = i < caps.length - 1 ? caps[i + 1].start : duration;
        let { start, end } = { start: d.s0, end: d.e0 };
        if (d.edge === "start") start = Math.min(Math.max(lo, d.s0 + dt), end - 0.2);
        else if (d.edge === "end") end = Math.max(Math.min(hi, d.e0 + dt), start + 0.2);
        else {
          const len = d.e0 - d.s0;
          start = Math.min(Math.max(lo, d.s0 + dt), hi - len);
          end = start + len;
        }
        const next = [...caps];
        const shift = d.edge === "move" && caps[i].words ? start - caps[i].start : 0;
        const words = shift ? caps[i].words.map((w) => ({ ...w, s: w.s + shift, e: w.e + shift })) : caps[i].words;
        next[i] = { ...caps[i], start: +start.toFixed(3), end: +end.toFixed(3), words };
        return next;
      });
    };
    const up = () => {
      const d = drag.current;
      drag.current = null;
      if (d && !d.moved) { seek(d.s0 + 0.01); setSelectedId(d.id); }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
  }, [zoom, duration, setCaptions, seek, setSelectedId]);

  const startDrag = (e, c, edge) => {
    e.stopPropagation();
    drag.current = { id: c.id, edge, x0: e.clientX, s0: c.start, e0: c.end, moved: false };
  };

  const step = zoom >= 80 ? 1 : zoom >= 30 ? 2 : 5;
  const ticks = [];
  for (let s = 0; s <= duration; s += step) ticks.push(s);

  return (
    <div className="px-3 md:px-5 py-3 border-b border-[var(--cc-border)]" data-testid="timeline">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">Timeline</h2>
        <div className="flex items-center gap-1">
          <button aria-label="Zoom out timeline" data-testid="timeline-zoom-out" onClick={() => setZoom((z) => Math.max(10, z / 1.5))}
            className="h-9 w-9 grid place-items-center rounded-md hover:bg-[var(--cc-elev)] text-[var(--cc-text-2)]"><ZoomOut className="h-4 w-4" /></button>
          <button aria-label="Zoom in timeline" data-testid="timeline-zoom-in" onClick={() => setZoom((z) => Math.min(200, z * 1.5))}
            className="h-9 w-9 grid place-items-center rounded-md hover:bg-[var(--cc-elev)] text-[var(--cc-text-2)]"><ZoomIn className="h-4 w-4" /></button>
        </div>
      </div>
      <div ref={scrollRef} className="overflow-x-auto cc-scroll rounded-md bg-[var(--cc-panel)] border border-[var(--cc-border)]">
        <div className="relative h-[76px] touch-pan-x" style={{ width }}
          onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); seek(Math.max(0, (e.clientX - r.left) / zoom)); }}>
          {ticks.map((s) => (
            <span key={s} className="absolute top-0 text-[10px] font-mono-ts text-[var(--cc-muted)] border-l border-[var(--cc-border)] pl-1 h-3 leading-3" style={{ left: s * zoom }}>
              {Math.floor(s / 60)}:{String(s % 60).padStart(2, "0")}
            </span>
          ))}
          {captions.map((c, i) => (
            <div key={c.id} data-testid={`timeline-block-${i}`} onPointerDown={(e) => startDrag(e, c, "move")} onClick={(e) => e.stopPropagation()}
              className={`absolute top-5 h-12 rounded px-1.5 text-[11px] leading-tight overflow-hidden cursor-grab select-none border deva ${c.id === selectedId ? "bg-amber-500/30 border-[var(--cc-accent)]" : c.flag ? "bg-rose-500/15 border-rose-500/50" : "bg-[var(--cc-hi)] border-[var(--cc-border-strong)]"}`}
              style={{ left: c.start * zoom, width: Math.max(6, (c.end - c.start) * zoom) }} title={c.text}>
              <span className="pointer-events-none line-clamp-2">{c.text}</span>
              <span onPointerDown={(e) => startDrag(e, c, "start")} aria-label="Drag caption start" className="absolute left-0 top-0 bottom-0 w-2.5 cursor-ew-resize bg-white/10 hover:bg-amber-400/60" />
              <span onPointerDown={(e) => startDrag(e, c, "end")} aria-label="Drag caption end" className="absolute right-0 top-0 bottom-0 w-2.5 cursor-ew-resize bg-white/10 hover:bg-amber-400/60" />
            </div>
          ))}
          <div className="absolute top-0 bottom-0 w-0.5 bg-[var(--cc-accent)] pointer-events-none" style={{ left: time * zoom }} data-testid="timeline-playhead" />
        </div>
      </div>
      {!captions.length && <p className="text-xs text-[var(--cc-muted)] mt-2">Captions will appear here after transcription. Drag a block to move it, drag its edges to change timing.</p>}
    </div>
  );
};
